import React from 'react';
import { Link } from 'react-router-dom';
import { Seo } from '../components/Seo';
import {
  Check,
  Sticker,
  Layers,
  Sparkles,
  Palette,
  Shirt,
} from 'lucide-react';

/**
 * Custom "label roll" icon — uses the operator's exact reference
 * artwork (saved to `/icons/label-roll.png`) rendered via a CSS
 * mask-image so the silhouette inherits `currentColor` from the
 * surrounding tile chrome. This guarantees a pixel-perfect match to
 * the reference while still tinting EG3 brand green (and darkening
 * to `#2F5D3A` on hover via the parent `group-hover` class).
 *
 * iter114s.55 — Switched from a hand-coded inline SVG to this masked
 * PNG approach after the operator confirmed the SVG renderings
 * weren't visually faithful enough. The reference PNG ships as a
 * static asset in /public/icons/.
 */
const LabelRollIcon = ({ className, ...props }) => (
  <div
    className={className}
    style={{
      backgroundColor: 'currentColor',
      WebkitMaskImage: 'url(/icons/label-roll.png)',
      maskImage: 'url(/icons/label-roll.png)',
      WebkitMaskSize: 'contain',
      maskSize: 'contain',
      WebkitMaskPosition: 'center',
      maskPosition: 'center',
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
    }}
    aria-hidden="true"
    {...props}
  />
);

/**
 * Product type tiles for the "Find Your Perfect Product" page.
 *
 * iter114s.54 — `Wearables` and `Badges` consolidated into a single
 * `Wearables` tile (Badges is a wearable subcategory and the routing
 * already pointed at /category/wearables). Final tile set: 6 entries.
 *
 * The blue PNG silhouettes (iter114s.49 era) were replaced in
 * iter114s.53 with lucide-react SVG icons coloured in EG3 brand green
 * and sized to fill the light-green section box. iter114s.54 swaps
 * the lucide `Tag` icon for a custom `LabelRollIcon` matching the
 * operator's reference artwork (a roll of labels peeling off).
 */
const PRODUCT_TYPES = [
  { id: 'stickers',       name: 'Stickers',         href: '/category/stickers',         Icon: Sticker },
  { id: 'labels',         name: 'Labels',           href: '/category/labels',           Icon: LabelRollIcon },
  { id: 'decals-clings',  name: 'Decals & Clings',  href: '/category/decals-clings',    Icon: Layers },
  { id: 'specialty',      name: 'Specialty',        href: '/category/specialty-niche',  Icon: Sparkles },
  { id: 'unique',         name: 'Unique Custom',    href: '/category/unique-custom',    Icon: Palette },
  { id: 'wearables',      name: 'Wearables',        href: '/category/wearables',        Icon: Shirt },
];

// Pill tracker steps. The "Format" + "Material" steps stay visually
// disabled here because this page only handles the Product Type pick;
// the next two are surfaced by the QuoteCalculator wizard once a type
// is chosen. Visual treatment matches the StickerYou reference: a
// filled radio for the active step, hollow circles for the upcoming
// steps, a thin connector line between them.
const PILL_STEPS = [
  { label: 'Product Type', state: 'active' },
  { label: 'Format',       state: 'pending' },
  { label: 'Material',     state: 'pending' },
];

const Products = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="products-page">
      <Seo
        title="All Custom Print Products — Stickers, Labels, Decals & More | Evergreen Graphics Group"
        description="Browse every custom product we print: stickers, roll labels, decals & clings, DTF wearables, magnets and specialty finishes. Instant quotes — Canada & USA shipping."
        path="/products"
      />
      {/* ================== HEADER + PILL TRACKER ================== */}
      <section className="border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 py-12 md:py-16 flex flex-wrap items-end justify-between gap-6">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#14281D]">
            Find <span className="text-[#6FBF73]">Your</span> Perfect Product
          </h1>
          <div className="flex items-center gap-3" data-testid="pill-tracker">
            {PILL_STEPS.map((step, idx) => (
              <React.Fragment key={step.label}>
                <div className="flex items-center gap-2">
                  <span
                    className={`w-4 h-4 rounded-full border-2 transition-colors ${
                      step.state === 'active'
                        ? 'bg-[#6FBF73] border-[#6FBF73] ring-4 ring-[#6FBF73]/20'
                        : 'bg-white border-[#C4D4C9]'
                    }`}
                    aria-hidden="true"
                  />
                  <span
                    className={`text-sm md:text-base ${
                      step.state === 'active' ? 'font-semibold text-[#14281D]' : 'text-[#9DB5A4]'
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
                {idx < PILL_STEPS.length - 1 && (
                  <span
                    className="hidden sm:inline-block w-10 md:w-16 h-px bg-[#C4D4C9]"
                    aria-hidden="true"
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ================== PRODUCT TYPE GRID ================== */}
      <section className="max-w-7xl mx-auto px-6 py-10 md:py-14">
        {/* iter114s.73 — Tile size halved (3-col → 6-col on lg+) per
            operator brief AND the icon now fills 80% of the green
            inner area. Padding removed from the icon container so the
            `w-4/5 h-4/5` percentage class lands on the full aspect-
            square parent — Tailwind's percentage utilities propagate
            to Lucide SVGs cleanly, giving us a proportional fill that
            works at every tile size. */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
          {PRODUCT_TYPES.map(({ id, name, href, Icon }) => (
            <Link
              key={id}
              to={href}
              className="group flex flex-col items-stretch bg-white border border-[#E5E7EB] rounded-2xl shadow-sm hover:shadow-md hover:border-[#6FBF73] hover:-translate-y-0.5 transition-all duration-300 overflow-hidden"
              data-testid={`product-type-card-${id}`}
            >
              <div className="aspect-square bg-[#E9F2EA] flex items-center justify-center transition-colors group-hover:bg-[#D7E8D9]">
                <Icon
                  className="w-4/5 h-4/5 text-[#6FBF73] group-hover:text-[#2F5D3A] transition-colors group-hover:scale-105 duration-300"
                  strokeWidth={1.4}
                  aria-hidden="true"
                />
              </div>
              <div className="px-3 py-2 border-t border-[#E5E7EB] text-center">
                <h3 className="text-xs md:text-sm font-semibold text-[#14281D] tracking-tight group-hover:text-[#2F5D3A] transition-colors">
                  {name}
                </h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ================== QUICK QUOTE LINK ================== */}
      <section className="max-w-7xl mx-auto px-6 pb-12">
        <p className="text-sm md:text-base text-[#1F3D2C]/80">
          Get a{' '}
          <Link
            to="/quote"
            className="text-[#6FBF73] hover:text-[#4A7C59] font-semibold underline-offset-4 hover:underline transition-colors"
            data-testid="quick-quote-link"
          >
            Quick Quote
          </Link>{' '}
          for pricing information
        </p>
      </section>

      {/* ================== TRUST BAR ================== */}
      <section className="bg-[#F4F7F2] py-12">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-6 text-[#14281D]">
          {[
            'Made in Canada — 100% chain of custody',
            'Free digital proof on every job',
            '100% satisfaction guarantee',
          ].map((line) => (
            <div key={line} className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#6FBF73] mt-0.5 flex-shrink-0" />
              <p className="text-sm md:text-base font-medium">{line}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#14281D] text-white py-10">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-emerald-100">© 2026 Evergreen Graphics Group Inc. All rights reserved.</p>
          <p className="text-sm text-emerald-200/80 mt-2">90 Venture Dr #2, Scarborough, ON M1B 3L6</p>
          <p className="text-sm text-emerald-200/80 mt-1">Phone: (905) 447-0257 | Email: info@eg3.ca</p>
        </div>
      </footer>
    </div>
  );
};

export default Products;
